import { Vue, Component, Watch } from 'vue-property-decorator'
import { ExStore } from 'vuex'
import { attachMediaStream } from '@/webrtc'
import { Device } from '@/store/videoCallRoom/types'
import * as MediaDeviceTypes from '@/webrtc/constants/mediaDeviceTypes'
import { MediaTypes } from '@/webrtc/constants/mediaTypes'

type VideoSignalListeners = {
  mute: (...args: any) => any
  unmute: (...args: any) => any
}

@Component
export default class HavingWebRTCMediaMixin extends Vue {
  readonly MAX_ATTACHMENT_RETRY_COUNT = 5
  readonly MUTE_EVENT_THROTTOLE_TIME = 5000

  $store!: ExStore

  muteTimerSetting?: number

  constraints = {
    audio: {
      echoCancellation: true
    }
  }

  remoteVideoSignalListeners: VideoSignalListeners = {
    mute: () => {
      this.muteTimerSetting = window.setTimeout(
        () => this.handleRemoteVideoSignalChange(true),
        this.MUTE_EVENT_THROTTOLE_TIME
      )
    },
    unmute: () => {
      window.clearTimeout(this.muteTimerSetting)
      this.handleRemoteVideoSignalChange(false)
    }
  }
  remoteVideoSignalMuted = false

  get selectedAudioOutputDevice() {
    return this.$store.getters['videoCallRoom/selectedDevices'][
      MediaDeviceTypes.AUDIO_OUTPUT
    ]
  }

  @Watch('selectedAudioOutputDevice')
  handleChangeOutputAudioDevice(device: Device) {
    const element = this.$refs.media as ExHTMLMediaElement

    this.attachAudioOutputDevice(element, device)
  }

  attachAudioOutputDevice(element: ExHTMLMediaElement, device?: Device) {
    if (!device) {
      return
    }

    // Chrome for Android, Android Webviewなどでサポートなし
    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/setSinkId
    if (typeof element.setSinkId !== 'function') {
      return
    }

    element.setSinkId(device.id)
  }

  attachStream(stream: MediaStream, muted: boolean, retry = 0) {
    if (!this.$refs.media) {
      if (retry > this.MAX_ATTACHMENT_RETRY_COUNT) {
        console.error(
          'Attaching streem failed becase it was tried too many times'
        )
        return
      }

      window.setTimeout(() => {
        this.attachStream(stream, muted, ++retry)
      }, 100)
      return
    }

    const element = this.$refs.media as ExHTMLMediaElement

    attachMediaStream(element, stream)

    element.muted = muted

    this.attachAudioOutputDevice(element, this.selectedAudioOutputDevice)
  }

  attachRemoteVideoSignalListeners(track: MediaStreamTrack) {
    Object.keys(this.remoteVideoSignalListeners).forEach(eventType => {
      track.addEventListener(
        eventType,
        this.remoteVideoSignalListeners[eventType as keyof VideoSignalListeners]
      )
    })
    this.remoteVideoSignalMuted = track.muted
  }

  detachRemoteVideoSignalListeners(track: MediaStreamTrack) {
    Object.keys(this.remoteVideoSignalListeners).forEach(eventType => {
      track.removeEventListener(
        eventType,
        this.remoteVideoSignalListeners[eventType as keyof VideoSignalListeners]
      )
    })
  }

  handleRemoteVideoSignalChange(on: boolean) {
    this.remoteVideoSignalMuted = on
  }

  initialize(
    stream: MediaStream,
    muted: boolean,
    mediaTypes: Array<MediaTypes> = [MediaTypes.video, MediaTypes.audio]
  ) {
    this.$nextTick(() => this.attachStream(stream, muted))

    if (mediaTypes.indexOf(MediaTypes.video) > -1) {
      stream.getVideoTracks().forEach(track => {
        this.attachRemoteVideoSignalListeners(track)
      })
    }
    stream
      .getAudioTracks()
      .forEach(track => track.applyConstraints(this.constraints.audio))
  }
}
